import { inject, Injectable } from '@angular/core';
import { displayMessageAction, tenantArchivedReadOnlyError, toAppId } from '@app/v2/shared/utils';
import { ApiError } from '@core/api-error';
import { AppErrorCode, getErrorMessage } from '@core/common-constants';
import { Action } from '@ngrx/store';
import {
  AllocateEntitlementOutcome,
  ApplicationMap,
  closeAllOpenDialogs,
  ControlPageFacade,
  CreateSubOrganizationOutcome,
  navigateTo,
  noLastAccessedTenant,
  openWebTechDialog,
  PlatformExpectationCase,
  setAllocateEntitlementResponseData,
  UiStateActions,
} from '@ra-state';
import { ActionButtonStyles, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { tap } from 'rxjs';
import { ApiEndPoints } from './data.service';

export function unknownErrorHandler(): Action {
  return UiStateActions.displayMessage({
    payload: {
      message: 'Something went wrong',
      type: 'Error',
      customBtn: {
        label: 'Go to Home',
        navigateTo: '/dashboard',
      },
    },
  });
}

export function showErrorDialog(messsage: string = '', title: string = 'Invite cannot be accepted'): Action {
  return openWebTechDialog({
    config: {
      title: title,
      message: messsage,
      messageType: NotificationType.Error,
      buttons: [
        {
          label: 'OK',
          buttonStyle: ActionButtonStyles.Main,
          shouldAutofocus: true,
        },
      ],
    },
    notificationType: NotificationType.Error,
  });
}
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlers {
  private readonly controlPageFacade = inject(ControlPageFacade);

  appsMap: ApplicationMap = {};

  // REVISIT: this is only used to get app name once
  appsMap$ = this.controlPageFacade.getAppsMap$
    .pipe(
      tap((appsMap) => {
        this.appsMap = appsMap;
      }),
    )
    .subscribe();

  handleEntitlementErrors = (apiError: ApiError, _body: any, context: any = {}): Action[] => {
    if (!apiError.Url.includes('/entitlements/')) {
      throw new Error('Url mismatch: API url does not match entitlements endpoint');
    }

    //// this entire switch case should be replaced with preconfigured matchers that are part of the
    /// command request builder
    /// it should match the error code (number/string/regex) and return a template string
    switch (apiError.ErrorCode) {
      case AppErrorCode.TenantServiceProvisioningPending: {
        const appId = toAppId(apiError.ErrorCodeParts[1]);
        const msg = getErrorMessage(apiError.ErrorCode, { ...context, appName: this.appsMap[appId]?.appName });
        return [
          UiStateActions.displayMessage({
            payload: {
              message: msg,
              type: 'Info',
              customBtn: {
                label: 'Go to Home',
                navigateTo: '/dashboard',
              },
            },
          }),
        ];
      }
      case AppErrorCode.ConsumptionNotFound: {
        const msg = 'Someone made changes while we were deallocating your entitlement. Please refresh and try again.';
        return [
          closeAllOpenDialogs(),
          UiStateActions.displayMessage({
            payload: {
              message: msg,
              type: 'Error',
              customBtn: {
                label: 'Refresh',
              },
            },
          }),
        ];
      }
      case AppErrorCode.EntitlementDeallocationErrorInvalidQuantity: {
        const msg =
          'The amount requested to deallocate is greater than the amount available. Please refresh and try again.';
        return [
          closeAllOpenDialogs(),
          UiStateActions.displayMessage({
            payload: {
              message: msg,
              type: 'Error',
              customBtn: {
                label: 'Refresh',
              },
            },
          }),
        ];
      }

      case AppErrorCode.Unknown: {
        const errorData: any = apiError.ErrorData as any;
        const {
          requestInfo: {
            payload: { platformExpectation },
          },
        } = context;
        let message = '';
        const actions: Action[] = [closeAllOpenDialogs()];

        switch (errorData.outcome) {
          case AllocateEntitlementOutcome.PlatformDeallocateFirst:
            message = `${context.requestInfo.organizationName} already has the maximum number of ${context.productFamilyName} entitlements. Deallocate any pending platform entitlements to allocate more.`;
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.AddonPlatformOverlapUnsatisfied:
            message = 'Add On entitlement does not overlap with platform entitlement date';
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.AddonPlatformUnsatisfied:
            message = `Can't allocate this entitlement when there is no active ${context.productFamilyName} product entitlement allocated.`;
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.QuantityExceedsAvailable:
            message =
              'The amount requested to allocate is greater than the amount available. Please refresh and try again';
            actions.push(
              UiStateActions.displayMessage({
                payload: {
                  message,
                  type: 'Error',
                  customBtn: {
                    label: 'Refresh',
                  },
                },
              }),
            );
            return actions;
          case AllocateEntitlementOutcome.PlatformExpectationMismatch &&
            platformExpectation.case !== PlatformExpectationCase.NoConflict:
            message = 'Someone made changes while we were allocating your entitlement. Please refresh and try again.';
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.ServiceProvisioningInProgress:
            message = `${context.productFamilyName} needs further action to be enabled for your organization. Please open to complete.`;
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.ConcurrentAllocationInProgress:
            message =
              'Service provisioning is in progress, please complete the provisioning before applying the entitlement.';
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.Invalid:
            message = 'Entitlement quantity must be one';
            actions.push(displayMessageAction(message));
            return actions;
          case AllocateEntitlementOutcome.TenantIsArchived: {
            actions.push(displayMessageAction(tenantArchivedReadOnlyError), noLastAccessedTenant());
            return actions;
          }
          case AllocateEntitlementOutcome.PlatformExpectationMismatch:
            return [setAllocateEntitlementResponseData({ payload: { errorData } })];
          case AllocateEntitlementOutcome.FirstEntitlementOfCSPServiceMustBeActive:
            message = 'This entitlement has a future start date and cannot be allocated.';
            return [closeAllOpenDialogs(), displayMessageAction(message)];
          default:
            message = getErrorMessage(apiError.ErrorCode, context);
            if (message) {
              return [displayMessageAction(message)];
            }
            return [];
        }
      }
      default: {
        const message = getErrorMessage(apiError.ErrorCode, context);
        if (message) {
          return [displayMessageAction(message)];
        }
        return [];
      }
    }
  };

  handleTrialErrors = (apiError: ApiError, _body: any): Action[] => {
    if (!apiError.Url.includes(ApiEndPoints.Trial)) {
      throw new Error('Url mismatch: API url does not match trial endpoint');
    }

    // eslint-disable-next-line sonarjs/no-small-switch
    switch (apiError.ErrorCode) {
      case AppErrorCode.TrialAlreadyRedeemed: {
        const config = {
          title: 'Trial already redeemed',
          message:
            'This trial link has already been used. Please check your account trials. If you still have questions, reach out to your sales representative.',
          buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Main }],
          showCloseIconButton: true,
        };

        return [
          openWebTechDialog({ config: config, notificationType: NotificationType.Error }),
          navigateTo({ path: '/dashboard' }),
        ];
      }
      default: {
        const config2 = {
          title: 'Invalid Trial Link',
          message: 'Please contact your sales representative for a new link. We apologize for the inconvenience.',
          buttons: [{ label: 'Close', buttonStyle: ActionButtonStyles.Main }],
          showCloseIconButton: true,
        };
        return [
          openWebTechDialog({ config: config2, notificationType: NotificationType.Error }),
          navigateTo({ path: '/dashboard' }),
        ];
      }
    }
  };

  handleLeaveTenantErrors = (apiError: ApiError, _body: any, context: any = {}): Action[] => {
    if (!apiError.Url.includes(ApiEndPoints.Roles)) {
      throw new Error('Url mismatch: API url does not match role endpoint');
    }

    switch (apiError.ErrorCode) {
      case AppErrorCode.CannotRemoveServiceOwner: {
        const message = 'You cannot leave this org because you have the owner role for at least one application.';
        return [
          UiStateActions.displayMessage({
            payload: {
              message,
              type: 'Error',
              customBtn: {
                label: 'Manage user access',
                navigateTo: '/access-mngmt',
              },
            },
          }),
        ];
      }
      case AppErrorCode.CannotRemoveOrgOwner: {
        return [displayMessageAction('You cannot leave this org because you are the owner of it.')];
      }
      default: {
        const message = getErrorMessage(apiError.ErrorCode);
        if (message) {
          return [displayMessageAction(message)];
        }
        return [
          UiStateActions.displayMessage({
            payload: {
              message: `We couldn't process your request to leave the ${context.tenantType}. This might be due to recent changes like archived ${context.tenantType} or updated permissions.`,
              type: 'Error',
              customBtn: {
                label: 'Refresh',
              },
            },
          }),
        ];
      }
    }
  };

  handleTransferOwnershipErrors = (apiError: ApiError, _body: any): Action[] => {
    if (!apiError.Url.includes(`${ApiEndPoints.Tenant}owners`)) {
      throw new Error('Url mismatch: API url does not match role endpoint');
    }

    return [
      UiStateActions.displayMessage({
        payload: {
          message: 'Ownership transfer failed. Please try again.',
          type: 'Error',
        },
      }),
    ];
  };

  handleTenantHierarchyErrors = (apiError: ApiError, _body: any): Action[] => {
    if (!apiError.Url.includes(`${ApiEndPoints.Tenant}hierarchy/`)) {
      throw new Error('Url mismatch: API url does not match role endpoint');
    }

    return [
      UiStateActions.displayMessage({
        payload: {
          message: 'We found some conflicts while updating your organization hierarchy. Refresh and try again.',
          type: 'Error',
        },
      }),
    ];
  };

  handleChildTenantErrors = (apiError: ApiError, _body: any): Action[] => {
    if (!apiError.Url.includes(ApiEndPoints.Tenant)) {
      throw new Error('Url mismatch: API url does not match tenant endpoint');
    }
    const errorData = apiError.ErrorData;
    const title = 'Sub-organization creation error';
    let errorMsg: string = '';
    const msgType: 'Error' | 'Info' = 'Error';
    switch (errorData.outcome) {
      case CreateSubOrganizationOutcome.ParentHasParent:
        errorMsg = 'Parent organization is already a child of another organization.';
        break;
      case CreateSubOrganizationOutcome.TenantNotFound:
        errorMsg = 'Parent organization not found.';
        break;
      case CreateSubOrganizationOutcome.TenantArchived:
        return [noLastAccessedTenant()];
      case CreateSubOrganizationOutcome.ParentLocked:
        errorMsg = 'Parent organization is locked and cannot have sub-organizations.';
        break;

      case CreateSubOrganizationOutcome.ParentCannotBePersonalTenant:
        errorMsg = 'Parent organization cannot be a personal organization.';
        break;

      default:
        errorMsg = 'Sub-organization creation failed, Please try again.';
        break;
    }
    return [
      UiStateActions.displayMessage({
        payload: {
          title,
          message: errorMsg,
          type: msgType,
        },
      }),
    ];
  };

  handleArchiveTenantErrors = (apiError: ApiError, _body: any, context: any = {}): Action[] => {
    if (!apiError.Url.includes(ApiEndPoints.Tenant)) {
      throw new Error('Url mismatch: API url does not match tenant endpoint');
    }
    if (apiError.ErrorCode === AppErrorCode.TenantIsArchived) {
      return [noLastAccessedTenant()];
    }
    return [
      UiStateActions.displayMessage({
        payload: {
          message: `We couldn't process your request to archive ${context.tenantName}. You may have lost access to it.`,
          type: 'Error',
          customBtn: {
            label: 'Refresh',
          },
        },
      }),
    ];
  };
}
